import * as React from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import {
  Box, Checkbox, CircularProgress, TablePagination, TableSortLabel,
  Tooltip,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import '../../styles/muiTablePagination.css'
import {
  TooltipStyle, CellTooltip, darkHeaderStyle, whiteHeaderStyle,
  TableContainerStyled, CustomPaper, flatTableStyle, CheckboxSelectAllTooltip,
} from './styles'
import theme from 'src/styles/theme'
import { Info } from 'react-feather'
import { CSSProperties } from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

interface Column {
  id: string;
  label: string | JSX.Element;
  backgroundColor?: string;
  width?: number | string;
  minWidth?: number | string;
  align?: 'right';
  format?: (value: number) => string;
  isDate?: boolean;
  testId?: string
  dateFormat?: string;
  useSorting?: boolean;
  limitTextAndShowInTooltip?: number;
}

interface Row {
  [key: string]: any;
}

interface TooltipContent {
  id: string
  label: string
}

type Order = 'asc' | 'desc';

interface Props {
  id?: string
  rows?: Row[]
  flatTable?: boolean
  columns: Column[]
  emptyLabel?: string
  tableLabel?: JSX.Element
  enablePagination?: boolean
  rowsPerPageLimit?: number
  rowsPerPageOptions?: number[]
  thinLine?: boolean
  tableFooter?: JSX.Element
  includeTooltipInto?: string
  tooltipContent?: TooltipContent[]
  columnsStyle?: CSSProperties;
  onRowDoubleClick?: (item: any) => void
  onRowClick?: (item: any) => void
  onChangePage?:(value: number) => void
  onChangeRowsPerPage?:(value: number) => void
  resizable?: boolean
  totalPages?: number
  containerStyle?: React.CSSProperties
  rowHeight?: number
  testId?: string
  currentPage?: number
  totalItems?: number
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedCount?: number;
  rowCount?: number;
  selected?: number[]
  hasCheckbox?: boolean
  onRowCheckboxClick?: (item: any) => void
  width?: string
  maxHeight?: string
  minHeight?: string
  hasSorting?: boolean
  resetPageOnRowsChange?: boolean
  getPaginatedItems?: (page: number, perPage: number, order?: string, orderBy?: string | number) => void
  checkboxSelectAllTooltipText?: string
  isLoading?: boolean
}

const TableMui = ({
  rows,
  columns,
  emptyLabel,
  tableLabel,
  enablePagination = false,
  rowsPerPageLimit = 30,
  rowsPerPageOptions = [5, 10, 25, 30],
  thinLine,
  tableFooter,
  includeTooltipInto,
  tooltipContent,
  columnsStyle,
  onRowClick,
  onRowDoubleClick,
  onChangePage,
  onChangeRowsPerPage,
  resizable,
  totalPages,
  containerStyle,
  rowHeight,
  testId = '',
  flatTable = false,
  currentPage,
  totalItems,
  id = '',
  selectedCount = 0,
  onSelectAllClick,
  rowCount = 0,
  selected,
  hasCheckbox = false,
  onRowCheckboxClick,
  width = '100%',
  maxHeight,
  minHeight,
  hasSorting = false,
  resetPageOnRowsChange = true,
  getPaginatedItems,
  checkboxSelectAllTooltipText = 'Ao selecionar, todas as páginas serão selecionadas',
  isLoading = false,
}: Props) => {
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageLimit)
  const [page, setPage] = React.useState(0)
  const [order, setOrder] = React.useState<Order>('desc')
  const [orderBy, setOrderBy] = React.useState<keyof Row>('')

  const dateColumnIds = React.useMemo(
    () => new Set(columns.filter((c) => c.isDate).map((c) => c.id)),
    [columns],
  )

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Row,
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    const newOrder = isAsc ? 'desc' : 'asc'
    setOrder(newOrder)
    setOrderBy(property)
    getPaginatedItems?.(1, rowsPerPage, newOrder, property)
  }

  const createSortHandler = (property: keyof Row) => (
    event: React.MouseEvent<unknown>,
  ) => {
    handleRequestSort(event, property)
  }

  function parseBrDate(value: string): Dayjs | null {
    const brDateRegex = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
    const match = value.match(brDateRegex)
    if (!match) return null
    const [, dd, mm, yyyy, hh = '0', min = '0', sec = '0'] = match
    return dayjs.utc(new Date(+yyyy, +mm - 1, +dd, +hh, +min, +sec))
  }

  function parseDateValue(value: string): number {
    const brDate = parseBrDate(value)
    if (brDate) return brDate.valueOf()
    const parsed = dayjs.utc(value)
    return parsed.isValid() ? parsed.valueOf() : 0
  }

  function descendingComparator<T>(a: T, b: T, orderByThis: keyof T): number {
    function atoi(text: string): number | string {
      return /^\d+$/.test(text) ? parseInt(text, 10) : text
    }

    function naturalKeys(value: string): (string | number)[] {
      return value.split(/(\d+)/).map(atoi)
    }

    const resolveNestedValue = (row: T, path: keyof T) => (path as string)
      .split('.')
      .reduce((acc: any, curr: string) => acc?.[curr], row)

    const aValue = (resolveNestedValue(a, orderByThis) as unknown as string)
    const bValue = (resolveNestedValue(b, orderByThis) as unknown as string)

    if (aValue && bValue) {
      if (dateColumnIds.has(orderByThis as string)) {
        const aTime = parseDateValue(aValue.toString())
        const bTime = parseDateValue(bValue.toString())
        if (bTime < aTime) return -1
        if (bTime > aTime) return 1
        return 0
      }

      const aKeys = naturalKeys(aValue.toString())
      const bKeys = naturalKeys(bValue.toString())

      const maxLength = Math.max(aKeys.length, bKeys.length)
      for (let i = 0; i < maxLength; i += 1) {
        if (aKeys[i] === undefined) return 1
        if (bKeys[i] === undefined) return -1
        if (typeof aKeys[i] === 'string' && typeof bKeys[i] === 'string') {
          const cmp = (bKeys[i] as string).localeCompare(aKeys[i] as string, 'pt-BR')
          if (cmp !== 0) return cmp
        } else {
          if (bKeys[i] < aKeys[i]) return -1
          if (bKeys[i] > aKeys[i]) return 1
        }
      }
    }
    return 0
  }

  function getComparator<Key extends keyof any>(
    orderOption: Order,
    orderByThis: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    if (!hasSorting) return () => 0

    return orderOption === 'desc'
      ? (a, b) => descendingComparator(a, b, orderByThis)
      : (a, b) => -descendingComparator(a, b, orderByThis)
  }

  React.useEffect(() => {
    if ((currentPage || currentPage === 0) && page !== currentPage) setPage(currentPage)
  }, [currentPage, page])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    onChangePage?.(newPage)
    getPaginatedItems?.(newPage + 1, rowsPerPage, order, orderBy)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parseRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(parseRowsPerPage)
    setPage(0)
    onChangeRowsPerPage?.(parseRowsPerPage)
    getPaginatedItems?.(1, parseRowsPerPage, order, orderBy)
  }

  const resolveHeaderColors = () => {
    if (tableLabel) return whiteHeaderStyle
    if (flatTable) return flatTableStyle
    return darkHeaderStyle
  }

  const resolveCellValue = (
    value: unknown,
    isDate?: boolean,
    dateFormat?: string,
    limitTextAndShowInTooltip?: number,
  ) => {
    const dateValue = value as string | number | Date | Dayjs
    if (isDate && dateValue) {
      const parsedDate = (typeof dateValue === 'string' && parseBrDate(dateValue)) || dayjs.utc(dateValue)
      if (parsedDate.isValid()) {
        return parsedDate.format(dateFormat ?? 'DD/MM/YYYY HH:mm')
      }
    }

    if (limitTextAndShowInTooltip && limitTextAndShowInTooltip > 0) {
      const textAux = value as string

      if (textAux.length > limitTextAndShowInTooltip) {
        return (
          <Tooltip title={textAux} placement='top' arrow>
            <span>{textAux.substring(0, limitTextAndShowInTooltip)}...</span>
          </Tooltip>
        )
      }
    }

    return (value === null || value === undefined || value === '' ? '-' : value)
  }

  const resolveRowCursor = () => (onRowClick || onRowDoubleClick ? 'pointer' : 'default')

  const Container = resizable ? TableContainerStyled : TableContainer

  const count = totalPages ? rowsPerPage * totalPages : rows?.length || 0

  const isSelected = (idSelect: number) => selected?.indexOf(idSelect) !== -1

  React.useEffect(() => {
    if (resetPageOnRowsChange) {
      setPage(0)
    }
  }, [resetPageOnRowsChange, rows])

  return (
    <CustomPaper
      $flatTable={flatTable}
      sx={{
        width, overflow: 'hidden', borderRadius: '8px', position: 'relative', ...containerStyle,
      }}
      elevation={flatTable ? 0 : 1}
    >
      <Container sx={{ maxHeight, minHeight }}>
        {tableLabel}
        <Table stickyHeader aria-label='sticky table' data-testid={testId} id={id}>
          <TableHead>
            <TableRow data-testid='table-header'>
              {
                hasCheckbox && (
                  <CheckboxSelectAllTooltip title={checkboxSelectAllTooltipText} placement='top' arrow>
                    <TableCell
                      padding='checkbox'
                      style={{
                        backgroundColor: resolveHeaderColors().backgroundColor,
                      }}
                    >
                      <Checkbox
                        indeterminate={selectedCount > 0 && selectedCount < rowCount}
                        checked={rowCount > 0 && selectedCount === rowCount}
                        onChange={onSelectAllClick}
                        style={{
                          ...resolveHeaderColors(),
                        }}
                        data-testid='table-header-checkbox'
                      />
                    </TableCell>
                  </CheckboxSelectAllTooltip>
                )
              }
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    padding: `${thinLine ? '4px' : ''} 16px`,
                    width: column.width,
                    minWidth: column.minWidth,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    ...resolveHeaderColors(),
                    ...columnsStyle,
                    backgroundColor: `${column.backgroundColor ? column.backgroundColor : resolveHeaderColors().backgroundColor}`,
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {
                    hasSorting && column.useSorting !== false
                      ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={createSortHandler(column.id)}
                          sx={
                            {
                              '&.MuiTableSortLabel-root': {
                                color: resolveHeaderColors().color,
                              },
                              '&.MuiTableSortLabel-root:hover': {
                                color: resolveHeaderColors().color,
                              },
                              '&.Mui-active': {
                                color: resolveHeaderColors().color,
                              },
                              '& .MuiTableSortLabel-icon': {
                                color: `${resolveHeaderColors().color} !important`,
                              },
                            }
                          }
                          data-testid={`sort-${column.testId}` || ''}
                        >
                          {column.label}
                          {orderBy === column.id ? (
                            <Box component='span' sx={visuallyHidden}>
                              {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      )
                      : (
                        <>
                          {column.label}
                        </>
                      )

                  }
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody data-testid='table-body'>
            {rows && rows.length
              ? (getPaginatedItems !== undefined
                ? rows
                : rows
                  .sort(getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ).map((row, index) => {
                const isItemSelected = isSelected(Object.values(row)[0])
                const labelId = `enhanced-table-checkbox-${index}`

                return (
                  <TableRow
                    hover
                    style={{ cursor: resolveRowCursor() }}
                    role='checkbox'
                    tabIndex={-1}
                    key={index}
                    onClick={onRowClick && (() => onRowClick(hasCheckbox ? Object.values(row)[0] : row))}
                    onDoubleClick={onRowDoubleClick && (() => onRowDoubleClick(row))}
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                    data-testid='table-row'
                  >
                    {
                      hasCheckbox && (
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                            onClick={onRowCheckboxClick && (() => onRowCheckboxClick(Object.values(row)[0]))}
                            data-testid='table-row-checkbox'
                          />
                        </TableCell>
                      )
                    }
                    {columns.map((column) => {
                      const value = column.id.split('.').reduce((acc, curr) => acc[curr], row)
                      const tooltipIntoCell = column.id === includeTooltipInto
                      const resolveToolTipContent = () => {
                        if (tooltipIntoCell && tooltipContent) {
                          const tooltipTitle = tooltipContent.map((content: TooltipContent) => <div>{content.label}: {row[content.id] || '-'}<br/></div>)

                          return (
                            <CellTooltip title={tooltipTitle} placement='top' arrow>
                              <Info size='16px' color={theme.colors.darkGray} />
                            </CellTooltip>
                          )
                        }
                        return ''
                      }

                      const resolveStyleWithTooltip = () => tooltipIntoCell && (TooltipStyle)

                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            padding: `${thinLine ? '4px' : ''} 16px`,
                            borderBottom: `1px solid ${theme.colors.gray}`,
                            textAlign: 'center',
                            height: rowHeight,
                            maxHeight: rowHeight,
                            minHeight: rowHeight,
                            ...resolveStyleWithTooltip(),
                          }}
                          data-testid={column.testId || ''}
                        >
                          {resolveCellValue(
                            value,
                            column.isDate,
                            column.dateFormat,
                            column.limitTextAndShowInTooltip,
                          )}
                          {resolveToolTipContent()}
                        </TableCell>

                      )
                    })}
                  </TableRow>
                )
              }) : emptyLabel
              && (
              <TableRow>
                {
                  hasCheckbox && <TableCell />
                }
                <TableCell colSpan={columns.length} style={{ textAlign: 'center' }} data-testid='empty-label-text'>
                  {emptyLabel}
                </TableCell>
              </TableRow>
              )}
          </TableBody>
        </Table>
      </Container>
      {enablePagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component='div'
          count={totalItems || count || rows?.length || 0}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage='Linhas por página:'
          labelDisplayedRows={({ from, to, count: countDisplayedRow }) => `${from}-${to} de ${countDisplayedRow}`}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      {tableFooter}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            zIndex: 2,
          }}
          data-testid='table-loading'
        >
          <CircularProgress size={40} />
        </Box>
      )}
    </CustomPaper>
  )
}

export default React.memo(TableMui)
